import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Upload, Database, FileSpreadsheet, CheckCircle, AlertCircle, BarChart3 } from 'lucide-react';

const COLLECTIONS = [
  { value: 'clients', label: 'Clients', desc: 'name_first, name_last, email, phone, address...' },
  { value: 'properties', label: 'Properties', desc: 'client_id, property_address_num, street, suburb...' },
  { value: 'appraisals', label: 'Appraisals', desc: 'client_id, property_id, status, quote_type...' },
  { value: 'leads', label: 'Leads', desc: 'contact_first_name, surname, phone, type...' },
  { value: 'contractors', label: 'Contractors', desc: 'contractor_name, company_name, phone...' },
  { value: 'roof_details', label: 'Roof Details', desc: 'property_id, roof_type, brand, colour...' },
  { value: 'quote_items', label: 'Quote Items', desc: 'appraisal_id, item, ref, materials, labour...' },
  { value: 'insurance_companies', label: 'Insurance Companies', desc: 'company_name, phone, email...' },
];

export default function DataMigrationPage() {
  const { api } = useAuth();
  const [stats, setStats] = useState({});
  const [collection, setCollection] = useState('clients');
  const [jsonData, setJsonData] = useState('');
  const [file, setFile] = useState(null);
  const [mode, setMode] = useState('csv');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);

  const loadStats = useCallback(async () => {
    try {
      const { data } = await api.get('/migrate/stats');
      setStats(data);
    } catch (e) { console.error(e); }
  }, [api]);

  useEffect(() => { loadStats(); }, [loadStats]);

  const handleCsvUpload = async () => {
    if (!file) { setError('Select a CSV file'); return; }
    setError('');
    setResult(null);
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await api.post(`/migrate/csv?collection=${collection}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setResult(data);
      loadStats();
    } catch (e) {
      setError(e.response?.data?.detail || 'Upload failed');
    }
    setUploading(false);
  };

  const handleJsonUpload = async () => {
    setError('');
    setResult(null);
    try {
      const records = JSON.parse(jsonData);
      if (!Array.isArray(records)) { setError('JSON must be an array of objects'); return; }
      setUploading(true);
      const { data } = await api.post('/migrate/upload', { collection, records });
      setResult(data);
      setJsonData('');
      loadStats();
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'Import failed');
    }
    setUploading(false);
  };

  return (
    <div data-testid="data-migration-page" className="space-y-6">
      <div>
        <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 font-[Manrope]">Data Migration</h1>
        <p className="text-sm text-slate-500 mt-1">Import data from your Access database via CSV or JSON</p>
      </div>
      {/* Stats Overview */}
      <div className="bg-white border border-[#E2E2DF] rounded-md p-5">
        <h3 className="text-lg font-semibold text-slate-800 font-[Manrope] flex items-center gap-2 mb-4"><BarChart3 size={18} className="text-[#4A6FA5]" /> Current Data</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {Object.entries(stats).map(([col, s]) => (
            <div key={col} className="bg-slate-50 p-3 rounded">
              <p className="text-xs font-bold uppercase tracking-wider text-slate-500">{col.replace(/_/g, ' ')}</p>
              <p className="text-xl font-bold text-slate-900">{s.total}</p>
              {s.imported > 0 && <p className="text-xs text-[#4A6FA5]">{s.imported} imported</p>}
            </div>
          ))}
        </div>
      </div>
      {/* Import Form */}
      <div className="bg-white border border-[#E2E2DF] rounded-md p-5 space-y-4">
        <h3 className="text-lg font-semibold text-slate-800 font-[Manrope] flex items-center gap-2"><Database size={18} className="text-[#D35400]" /> Import Data</h3>
        <div>
          <label className="block text-xs font-bold uppercase tracking-[0.15em] text-slate-500 mb-2">Target Collection</label>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {COLLECTIONS.map(c => (
              <button key={c.value} data-testid={`collection-${c.value}`} onClick={() => setCollection(c.value)} className={`p-3 rounded-md border text-left transition-all ${collection === c.value ? 'border-[#D35400] bg-[#D35400]/5' : 'border-[#E2E2DF] hover:border-slate-300'}`}>
                <p className="text-sm font-semibold text-slate-700">{c.label}</p>
                <p className="text-[10px] text-slate-400 mt-0.5">{c.desc}</p>
              </button>
            ))}
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setMode('csv')} className={`px-4 py-2 rounded-md text-sm font-medium border transition-all ${mode === 'csv' ? 'border-[#D35400] bg-[#D35400]/5 text-[#D35400]' : 'border-[#E2E2DF] text-slate-500'}`}><FileSpreadsheet size={14} className="inline mr-1" />CSV Upload</button>
          <button onClick={() => setMode('json')} className={`px-4 py-2 rounded-md text-sm font-medium border transition-all ${mode === 'json' ? 'border-[#D35400] bg-[#D35400]/5 text-[#D35400]' : 'border-[#E2E2DF] text-slate-500'}`}><Database size={14} className="inline mr-1" />JSON Paste</button>
        </div>
        {mode === 'csv' ? (
          <div className="space-y-3">
            <p className="text-xs text-slate-500">Upload a CSV file exported from Access. First row must be column headers.</p>
            <div className="border-2 border-dashed border-[#E2E2DF] rounded-md p-6 text-center">
              <input data-testid="csv-file-input" type="file" accept=".csv" onChange={e => setFile(e.target.files[0])} className="block mx-auto text-sm" />
              {file && <p className="text-sm text-slate-600 mt-2">{file.name} ({(file.size / 1024).toFixed(1)} KB)</p>}
            </div>
            <button data-testid="upload-csv-button" onClick={handleCsvUpload} disabled={uploading || !file} className="flex items-center gap-2 px-4 py-2 bg-[#D35400] hover:bg-[#A04000] text-white rounded-md text-sm font-semibold disabled:opacity-50">
              <Upload size={14} /> {uploading ? 'Uploading...' : 'Upload CSV'}
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-xs text-slate-500">Paste a JSON array of objects. Example: [&#123;"name_first": "John", "name_last": "Smith"&#125;]</p>
            <textarea data-testid="json-input" value={jsonData} onChange={e => setJsonData(e.target.value)} rows={8} placeholder={'[{"name_first": "John", "name_last": "Smith", "email": "john@test.com"}]'} className="w-full px-3 py-2 border border-[#E2E2DF] rounded-md text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#D35400]" />
            <button data-testid="upload-json-button" onClick={handleJsonUpload} disabled={uploading || !jsonData.trim()} className="flex items-center gap-2 px-4 py-2 bg-[#D35400] hover:bg-[#A04000] text-white rounded-md text-sm font-semibold disabled:opacity-50">
              <Upload size={14} /> {uploading ? 'Importing...' : 'Import JSON'}
            </button>
          </div>
        )}
        {error && <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700"><AlertCircle size={16} />{error}</div>}
        {result && (
          <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-md text-sm text-green-700" data-testid="import-result">
            <CheckCircle size={16} />
            Successfully imported <strong>{result.imported}</strong> records into <strong>{result.collection}</strong>
            {result.fields && <span className="text-xs text-green-600 ml-1">(fields: {result.fields.slice(0, 5).join(', ')}...)</span>}
          </div>
        )}
      </div>
      {/* Migration Guide */}
      <div className="bg-white border border-[#E2E2DF] rounded-md p-5">
        <h3 className="text-lg font-semibold text-slate-800 font-[Manrope] mb-3">Migration Guide</h3>
        <div className="space-y-3 text-sm text-slate-600">
          <div className="p-3 bg-slate-50 rounded">
            <p className="font-semibold text-slate-700 mb-1">Step 1: Export from Access</p>
            <p>Open your Access database, right-click each table, and choose "Export" &gt; "Text File" &gt; save as CSV with headers.</p>
          </div>
          <div className="p-3 bg-slate-50 rounded">
            <p className="font-semibold text-slate-700 mb-1">Step 2: Map Fields</p>
            <p>Ensure your CSV column headers match the expected field names. The system will auto-detect data types.</p>
          </div>
          <div className="p-3 bg-slate-50 rounded">
            <p className="font-semibold text-slate-700 mb-1">Step 3: Import Order</p>
            <p>Import in this order: 1) Clients, 2) Properties, 3) Roof Details, 4) Appraisals, 5) Quote Items, 6) Leads, 7) Contractors.</p>
          </div>
          <div className="p-3 bg-amber-50 rounded border border-amber-200">
            <p className="font-semibold text-amber-700 mb-1">Note: Linking Records</p>
            <p className="text-amber-700">For related records (e.g., properties to clients), you'll need to include the parent record's MongoDB _id as client_id in your import data.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
